"use client";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { DollarSign, Square } from "lucide-react";
import { ResponsiveCurrency } from "./responsive-currency";

interface PropertyInfoSectionProps {
  propertyPrice: number;
  pricePerSqFt: number;
  squareFeet: number;
  yearBuilt: number | string;
  bedrooms: number;
  bathrooms: number;
  zipCode?: string;
  marketTrends?: {
    medianSalePrice?: string;
    medianSalePricePerSqFt?: string;
  };
  className?: string;
}

// Helper function to parse price strings like "$1.02M" or "$956K"
function parsePriceString(priceStr?: string): number | null {
  if (!priceStr) return null;

  try {
    const cleanPrice = priceStr.replace(/[$,]/g, "");
    if (cleanPrice.includes("K")) {
      return Number.parseFloat(cleanPrice.replace("K", "")) * 1000;
    }
    if (cleanPrice.includes("M")) {
      return Number.parseFloat(cleanPrice.replace("M", "")) * 1000000;
    }
    return Number.parseFloat(cleanPrice);
  } catch (error) {
    return null;
  }
}

function PropertyMetric({
  icon,
  label,
  value,
  marketValue,
  zipCode,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode | number;
  marketValue?: string;
  zipCode?: string;
}) {
  let comparisonElement = null;
  let numberValue: number | null = null;

  if (typeof value === "number") {
    numberValue = value;
  }

  if (marketValue && numberValue !== null) {
    const marketValueNum = parsePriceString(marketValue);
    if (marketValueNum && marketValueNum > 0) {
      const diff = numberValue - marketValueNum;
      const percentDiff = Math.round((diff / marketValueNum) * 100);
      const isAboveMarket = diff > 0;

      comparisonElement = (
        <div className="flex items-center gap-1 mt-1">
          <span
            className={cn(
              "text-xs font-semibold px-1.5 py-0.5 rounded-full",
              isAboveMarket
                ? "bg-amber-50 text-amber-700 border border-amber-200/50"
                : "bg-emerald-50 text-emerald-700 border border-emerald-200/50",
            )}
          >
            {isAboveMarket ? "+" : ""}
            {percentDiff}%
          </span>
          <span className="text-xs text-gray-500">
            vs {zipCode ?? "median"}
          </span>
        </div>
      );
    }
  }

  const displayValue =
    typeof value === "number" ? <ResponsiveCurrency value={value} /> : value;

  return (
    <div className="flex-1 sm:min-w-[200px]">
      <div className="flex items-center gap-2 text-gray-500 mb-1.5">
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-50">
          {icon}
        </div>
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="text-2xl font-semibold tracking-tight">
        {displayValue}
      </div>
      {marketValue && (
        <div className="flex items-center gap-2 mt-1">{comparisonElement}</div>
      )}
    </div>
  );
}

export function PropertyInfoSection({
  propertyPrice,
  pricePerSqFt,
  zipCode,
  marketTrends,
  className,
}: PropertyInfoSectionProps) {
  return (
    <Card
      className={cn("overflow-hidden bg-white/50 backdrop-blur-sm", className)}
    >
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">
          Property Overview
        </h2>
      </div>
      <div className="p-6">
        <div className="flex flex-row gap-8">
          <PropertyMetric
            icon={<DollarSign className="h-4 w-4 text-gray-500" />}
            label="List Price"
            value={propertyPrice}
            marketValue={marketTrends?.medianSalePrice}
            zipCode={zipCode}
          />
          <PropertyMetric
            icon={<Square className="h-4 w-4 text-gray-500" />}
            label="Price/Sq.Ft"
            value={pricePerSqFt}
            marketValue={marketTrends?.medianSalePricePerSqFt}
            zipCode={zipCode}
          />
        </div>
      </div>
    </Card>
  );
}
